import { useState, useCallback, useEffect, useRef } from 'react';
import {
  makeStyles,
  Box,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
  Snackbar,
  Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import RateReviewIcon from '@material-ui/icons/RateReview';
import AssignmentIcon from '@material-ui/icons/Assignment';
import GroupIcon from '@material-ui/icons/Group';
import { useApi, githubAuthApiRef, configApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { Octokit } from '@octokit/rest';
import { PullRequestReview, PullRequestComment, PullRequestReviewItem } from '../../types';
import { CollapsibleSection } from '../CollapsibleSection';
import { PullRequestListItem } from './PullRequestListItem';
import { githubClientCache } from '../../utils/githubCache';

const useStyles = makeStyles(theme => ({
  subsection: {
    marginBottom: theme.spacing(2),
  },
  subsectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  prList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  refreshButton: {
    marginLeft: theme.spacing(1),
  },
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  errorMessage: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '20',
    borderRadius: theme.shape.borderRadius,
  },
  count: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  urgentCount: {
    backgroundColor: theme.palette.error.main,
  },
  teamCount: {
    backgroundColor: theme.palette.info.main,
  },
  teamChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  teamChip: {
    height: 18,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.info.light + '30',
    borderColor: theme.palette.info.main,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  },
}));

export interface PullRequestsSectionProps {
  defaultCollapsed?: boolean;
  editMode?: boolean;
  onHide?: () => void;
  maxPRs?: number;
}

// Extract JIRA ticket patterns from text
const extractJiraTickets = (text: string): string[] => {
  const jiraPattern = /\b([A-Z][A-Z0-9]+-\d+)\b/g;
  const matches = text.match(jiraPattern) || [];
  return [...new Set(matches)];
};

// Extract Terraform workspace from text
const extractTerraformWorkspace = (text: string): string | undefined => {
  const patterns = [
    /workspace[:\s=]+([a-zA-Z0-9_-]+)/i,
    /TF_WORKSPACE[=:\s]+([a-zA-Z0-9_-]+)/i,
    /terraform\s+workspace\s+([a-zA-Z0-9_-]+)/i,
    /\[([a-zA-Z0-9_-]+)\s*workspace\]/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return undefined;
};

// Sort PRs: non-drafts first, then by updated date
const sortPRs = (prs: PullRequestReview[]): PullRequestReview[] => {
  return [...prs].sort((a, b) => {
    // Non-drafts first
    if (a.draft !== b.draft) {
      return a.draft ? 1 : -1;
    }
    // Then by updated date (newest first)
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
};

// Process items in batches to avoid overwhelming the API (controlled concurrency)
async function processBatched<T, R>(
  items: T[],
  processor: (item: T) => Promise<R>,
  batchSize: number = 5
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(processor));
    results.push(...batchResults);
  }
  return results;
}

export const PullRequestsSection = ({
  defaultCollapsed = false,
  editMode = false,
  onHide,
  maxPRs = 10,
}: PullRequestsSectionProps) => {
  const classes = useStyles();
  const githubAuthApi = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);

  const [reviewRequestedPRs, setReviewRequestedPRs] = useState<PullRequestReview[]>([]);
  const [teamReviewPRs, setTeamReviewPRs] = useState<PullRequestReview[]>([]);
  const [userRelatedPRs, setUserRelatedPRs] = useState<PullRequestReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [octokit, setOctokit] = useState<Octokit | null>(null);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get default organization from config
  const getDefaultOrg = useCallback(() => {
    let defaultOrg = configApi.getOptionalString('catalog.providers.github.providerId.organization');
    if (!defaultOrg) {
      const githubOrgList = configApi.getOptionalStringArray('catalog.providers.githubOrg.orgs');
      if (githubOrgList && githubOrgList.length > 0) {
        defaultOrg = githubOrgList[0];
      }
    }
    return defaultOrg || 'badal-io';
  }, [configApi]);

  // Cache for repo settings within a single fetch cycle to avoid duplicate API calls
  const repoSettingsCacheRef = useRef<Map<string, any>>(new Map());

  const fetchPullRequests = useCallback(async (isRefresh = false, retryCount = 0) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        // Clear client cache on manual refresh
        githubClientCache.clearAll();
      } else {
        setLoading(true);
      }
      setError(null);

      // Clear per-fetch repo settings cache
      repoSettingsCacheRef.current.clear();

      const accessToken = await githubAuthApi.getAccessToken(['repo', 'read:org', 'read:user']);
      if (!accessToken) {
        throw new Error('Unable to get GitHub access token. Please sign in with GitHub.');
      }

      const client = new Octokit({ auth: accessToken });
      setOctokit(client);

      let user;
      try {
        const response = await client.users.getAuthenticated();
        user = response.data;
      } catch (authErr: any) {
        // Handle "Bad credentials" by retrying once with fresh token
        if (authErr?.status === 401 && retryCount < 1) {
          console.warn('GitHub token may be stale, retrying...');
          await new Promise(resolve => setTimeout(resolve, 500));
          return fetchPullRequests(isRefresh, retryCount + 1);
        }
        throw authErr;
      }
      setCurrentUser(user.login);

      const defaultOrg = getDefaultOrg();

      // Fetch user's teams for team review requests
      let userTeams: Array<{ slug: string; name: string; organization: { login: string } }> = [];
      try {
        const { data: teams } = await client.request('GET /user/teams', {
          per_page: 100,
        });
        userTeams = teams.filter((t: any) => t.organization.login.toLowerCase() === defaultOrg.toLowerCase());
      } catch (err) {
        console.warn('Failed to fetch user teams:', err);
      }

      // Fetch all search queries in parallel to reduce total request time
      const [reviewRequestedResponse, authoredResponse, reviewedResponse, commentedResponse] = await Promise.all([
        // Fetch PRs where user is requested as reviewer
        client.search.issuesAndPullRequests({
          q: `is:pr is:open review-requested:${user.login} org:${defaultOrg}`,
          sort: 'updated',
          order: 'desc',
          per_page: maxPRs,
        }),
        // Fetch PRs authored by user
        client.search.issuesAndPullRequests({
          q: `is:pr is:open author:${user.login} org:${defaultOrg}`,
          sort: 'updated',
          order: 'desc',
          per_page: maxPRs,
        }),
        // Fetch PRs where user has reviewed
        client.search.issuesAndPullRequests({
          q: `is:pr is:open reviewed-by:${user.login} org:${defaultOrg}`,
          sort: 'updated',
          order: 'desc',
          per_page: maxPRs,
        }),
        // Fetch PRs where user has commented
        client.search.issuesAndPullRequests({
          q: `is:pr is:open commenter:${user.login} org:${defaultOrg}`,
          sort: 'updated',
          order: 'desc',
          per_page: maxPRs,
        }),
      ]);

      // Fetch PRs where user's teams are requested as reviewers
      // Use Promise.all for parallel fetching to reduce total time and avoid sequential rate limiting
      // Map of PR number -> { item, teams: string[] }
      const teamReviewPRMap = new Map<number, { item: any; teams: string[] }>();

      // Fetch all team reviews in parallel (limit to 5 concurrent to avoid rate limits)
      const teamBatches: typeof userTeams[] = [];
      for (let i = 0; i < userTeams.length; i += 5) {
        teamBatches.push(userTeams.slice(i, i + 5));
      }

      for (const batch of teamBatches) {
        const teamResults = await Promise.all(
          batch.map(async team => {
            try {
              const teamReviewResponse = await client.search.issuesAndPullRequests({
                q: `is:pr is:open team-review-requested:${team.organization.login}/${team.slug} org:${defaultOrg}`,
                sort: 'updated',
                order: 'desc',
                per_page: maxPRs,
              });
              return { team, items: teamReviewResponse.data.items };
            } catch (err) {
              console.warn(`Failed to fetch team review PRs for ${team.slug}:`, err);
              return { team, items: [] };
            }
          })
        );

        for (const { team, items } of teamResults) {
          for (const item of items) {
            if (teamReviewPRMap.has(item.number)) {
              teamReviewPRMap.get(item.number)!.teams.push(team.slug);
            } else {
              teamReviewPRMap.set(item.number, { item, teams: [team.slug] });
            }
          }
        }
      }

      // Process review-requested PRs with controlled concurrency (5 at a time)
      const processedReviewRequested = await processBatched(
        reviewRequestedResponse.data.items,
        (item) => processPR(client, item, 'review_requested'),
        5
      );

      // Get PR numbers that user is personally requested to review
      const reviewRequestedNumbers = new Set(reviewRequestedResponse.data.items.map(i => i.number));

      // Process team review PRs (exclude ones where user is personally requested)
      const teamReviewPRsToProcess = Array.from(teamReviewPRMap.values())
        .filter(({ item }) => !reviewRequestedNumbers.has(item.number));

      // Process team review PRs with controlled concurrency (5 at a time)
      const processedTeamReviewPRs = await processBatched(
        teamReviewPRsToProcess.slice(0, maxPRs),
        async ({ item, teams }) => {
          const pr = await processPR(client, item, 'team_review_requested');
          if (pr) {
            pr.requestingTeamSlugs = teams;
          }
          return pr;
        },
        5
      );

      // Get team review PR numbers for deduplication
      const teamReviewNumbers = new Set(teamReviewPRsToProcess.map(({ item }) => item.number));

      // Combine user-related PRs and deduplicate
      const allUserRelated = [
        ...authoredResponse.data.items.map(item => ({ item, relationship: 'author' as const })),
        ...reviewedResponse.data.items.map(item => ({ item, relationship: 'reviewer' as const })),
        ...commentedResponse.data.items.map(item => ({ item, relationship: 'commenter' as const })),
      ];

      // Deduplicate by PR number, keeping first occurrence (author > reviewer > commenter priority)
      const seenPRs = new Set<number>();
      const uniqueUserRelated: Array<{ item: typeof allUserRelated[0]['item']; relationship: 'author' | 'reviewer' | 'commenter' }> = [];
      for (const { item, relationship } of allUserRelated) {
        if (!seenPRs.has(item.number)) {
          seenPRs.add(item.number);
          uniqueUserRelated.push({ item, relationship });
        }
      }

      // Filter out PRs that are already in review-requested or team-review list
      const filteredUserRelated = uniqueUserRelated
        .filter(({ item }) => !reviewRequestedNumbers.has(item.number) && !teamReviewNumbers.has(item.number))
        .slice(0, maxPRs);

      // Process user-related PRs with controlled concurrency (5 at a time)
      const processedUserRelated = await processBatched(
        filteredUserRelated.slice(0, maxPRs),
        ({ item, relationship }) => processPR(client, item, relationship),
        5
      );

      // Filter out nulls and sort (drafts at bottom)
      const validReviewRequested = sortPRs(processedReviewRequested.filter((pr): pr is PullRequestReview => pr !== null));
      const validTeamReviewPRs = sortPRs(processedTeamReviewPRs.filter((pr): pr is PullRequestReview => pr !== null));
      const validUserRelated = sortPRs(processedUserRelated.filter((pr): pr is PullRequestReview => pr !== null));

      setReviewRequestedPRs(validReviewRequested);
      setTeamReviewPRs(validTeamReviewPRs);
      setUserRelatedPRs(validUserRelated);
    } catch (err) {
      console.error('Failed to fetch pull requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch pull requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [githubAuthApi, getDefaultOrg, maxPRs]);

  // Process a single PR item into PullRequestReview
  const processPR = async (
    client: Octokit,
    item: any,
    relationship: PullRequestReview['relationship']
  ): Promise<PullRequestReview | null> => {
    try {
      // Extract owner/repo from repository_url
      const repoUrlParts = item.repository_url.split('/');
      const owner = repoUrlParts[repoUrlParts.length - 2];
      const repo = repoUrlParts[repoUrlParts.length - 1];
      const repoKey = `${owner}/${repo}`.toLowerCase();

      // Check client cache for repo metadata first
      let cachedRepoMeta = githubClientCache.getRepoMetadata(owner, repo);

      // Check per-fetch cache for repo settings (avoids duplicate calls within same fetch)
      let repoDetails = repoSettingsCacheRef.current.get(repoKey);

      // Fetch PR details - always needed for fresh data (draft state, mergeable, etc.)
      const prPromise = client.pulls.get({
        owner,
        repo,
        pull_number: item.number,
      });

      // Only fetch repo settings if not in cache
      const repoPromise = repoDetails ? Promise.resolve({ data: repoDetails }) : client.repos.get({
        owner,
        repo,
      });

      const [prResponse, repoResponse] = await Promise.all([prPromise, repoPromise]);

      const prDetails = prResponse.data;
      repoDetails = repoResponse.data;

      // Cache repo settings for this fetch cycle (avoids duplicate calls for same repo)
      if (!repoSettingsCacheRef.current.has(repoKey)) {
        repoSettingsCacheRef.current.set(repoKey, repoDetails);
      }

      // Update client-side cache for repo metadata
      if (!cachedRepoMeta) {
        githubClientCache.setRepoMetadata(owner, repo, {
          owner,
          name: repo,
          fullName: `${owner}/${repo}`,
          language: repoDetails.language,
          defaultBranch: repoDetails.default_branch,
          allowMergeCommit: repoDetails.allow_merge_commit ?? true,
          allowSquashMerge: repoDetails.allow_squash_merge ?? true,
          allowRebaseMerge: repoDetails.allow_rebase_merge ?? true,
          stargazersCount: repoDetails.stargazers_count ?? 0,
        });
      }

      // Fetch reviews to determine approval status
      let isApproved = false;
      let approvalCount = 0;
      let reviewCount = 0;
      try {
        const { data: reviews } = await client.pulls.listReviews({
          owner,
          repo,
          pull_number: item.number,
        });

        // Count approvals (only the latest review per user counts)
        const latestReviewByUser = new Map<string, string>();
        for (const review of reviews) {
          if (review.user && review.state) {
            latestReviewByUser.set(review.user.login, review.state);
          }
        }

        approvalCount = Array.from(latestReviewByUser.values()).filter(state => state === 'APPROVED').length;
        isApproved = approvalCount > 0;
        reviewCount = reviews.length;
      } catch {
        // Reviews might not be accessible
      }

      // Fetch comments count (only issue comments, not review comments)
      // Use cached count if available
      let commentCount = githubClientCache.getCommentCount(owner, repo, item.number);
      if (commentCount === null) {
        try {
          const { data: comments } = await client.issues.listComments({
            owner,
            repo,
            issue_number: item.number,
            per_page: 100,
          });
          // Filter out bot comments that are status checks (e.g., from CI systems)
          commentCount = comments.filter(c => {
            const isBot = c.user?.type === 'Bot';
            // Keep all human comments and bot comments that aren't CI status updates
            if (!isBot) return true;
            // Filter out common CI bot patterns
            const ciPatterns = ['coverage', 'build', 'test', 'ci', 'check'];
            const body = (c.body || '').toLowerCase();
            return !ciPatterns.some(p => body.includes(p));
          }).length;
          // Cache the count
          githubClientCache.setCommentCount(owner, repo, item.number, commentCount);
        } catch {
          // Comments might not be accessible
          commentCount = 0;
        }
      }

      // Fetch combined status for the head commit
      let statusChecks: PullRequestReview['statusChecks'] | undefined;
      try {
        const { data: combinedStatus } = await client.repos.getCombinedStatusForRef({
          owner,
          repo,
          ref: prDetails.head.sha,
        });

        // Also get check runs
        const { data: checkRuns } = await client.checks.listForRef({
          owner,
          repo,
          ref: prDetails.head.sha,
        });

        const totalCount = combinedStatus.statuses.length + checkRuns.total_count;
        const successCount = combinedStatus.statuses.filter(s => s.state === 'success').length +
          checkRuns.check_runs.filter(c => c.conclusion === 'success').length;
        const failureCount = combinedStatus.statuses.filter(s => s.state === 'failure').length +
          checkRuns.check_runs.filter(c => c.conclusion === 'failure').length;
        const pendingCount = totalCount - successCount - failureCount;

        // Find first failed check URL
        let firstFailedCheckUrl: string | undefined;
        const failedStatus = combinedStatus.statuses.find(s => s.state === 'failure');
        const failedCheck = checkRuns.check_runs.find(c => c.conclusion === 'failure');
        if (failedStatus) {
          firstFailedCheckUrl = failedStatus.target_url || undefined;
        } else if (failedCheck) {
          firstFailedCheckUrl = failedCheck.html_url || undefined;
        }

        // Determine overall state
        let state: 'success' | 'failure' | 'pending' | 'error' = 'pending';
        if (failureCount > 0) {
          state = 'failure';
        } else if (pendingCount === 0 && successCount > 0) {
          state = 'success';
        } else if (totalCount === 0) {
          state = 'success'; // No checks required
        }

        statusChecks = {
          state,
          totalCount,
          successCount,
          failureCount,
          pendingCount,
          firstFailedCheckUrl,
          checksUrl: `${prDetails.html_url}/checks`,
        };
      } catch {
        // Status checks might not be available
      }

      // Extract JIRA tickets and Terraform workspace from title, body, and branch
      const searchText = `${item.title || ''} ${item.body || ''} ${prDetails.head.ref || ''}`;
      const jiraTickets = extractJiraTickets(searchText);
      const terraformWorkspace = extractTerraformWorkspace(searchText);

      // Determine allowed merge methods from repo settings
      const allowedMergeMethods = {
        merge: repoDetails.allow_merge_commit ?? true,
        squash: repoDetails.allow_squash_merge ?? true,
        rebase: repoDetails.allow_rebase_merge ?? true,
      };

      // Determine if PR is ready to merge
      const checksPass = !statusChecks || statusChecks.state === 'success';
      const mergeable = prDetails.mergeable === true;
      const isReadyToMerge = !prDetails.draft && isApproved && checksPass && mergeable;

      // Extract requested reviewers
      const requestedReviewers: PullRequestReview['requestedReviewers'] = [
        ...((prDetails.requested_reviewers || []).map((r: any) => ({
          type: 'user' as const,
          login: r.login,
          displayName: r.login,
        }))),
        ...((prDetails.requested_teams || []).map((t: any) => ({
          type: 'team' as const,
          login: t.slug,
          displayName: t.name || t.slug,
        }))),
      ];

      return {
        id: item.id,
        number: item.number,
        title: item.title,
        state: item.state,
        htmlUrl: item.html_url,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        draft: prDetails.draft || false,
        nodeId: prDetails.node_id,
        author: {
          login: item.user?.login || 'unknown',
          avatarUrl: item.user?.avatar_url || '',
        },
        repository: {
          owner,
          name: repo,
          fullName: `${owner}/${repo}`,
        },
        headRef: prDetails.head.ref,
        headSha: prDetails.head.sha,
        baseRef: prDetails.base.ref,
        additions: prDetails.additions,
        deletions: prDetails.deletions,
        changedFiles: prDetails.changed_files,
        statusChecks,
        jiraTickets: jiraTickets.length > 0 ? jiraTickets : undefined,
        terraformWorkspace,
        relationship,
        isApproved,
        approvalCount,
        commentCount,
        reviewCount,
        mergeable,
        mergeableState: prDetails.mergeable_state || undefined,
        allowedMergeMethods,
        isReadyToMerge,
        requestedReviewers,
      };
    } catch (err) {
      console.error(`Failed to process PR #${item.number}:`, err);
      return null;
    }
  };

  // Handle merge action
  const handleMerge = useCallback(async (pr: PullRequestReview, mergeMethod: 'merge' | 'squash' | 'rebase') => {
    if (!octokit) return;

    try {
      await octokit.pulls.merge({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        pull_number: pr.number,
        merge_method: mergeMethod,
      });

      setSnackbar({
        open: true,
        message: `PR #${pr.number} merged successfully!`,
        severity: 'success',
      });

      // Refresh the list
      fetchPullRequests(true);
    } catch (err) {
      console.error('Failed to merge PR:', err);
      setSnackbar({
        open: true,
        message: `Failed to merge PR #${pr.number}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  }, [octokit, fetchPullRequests]);

  // Handle mark ready action (convert draft to ready for review)
  const handleMarkReady = useCallback(async (pr: PullRequestReview) => {
    if (!octokit) return;

    try {
      const accessToken = await githubAuthApi.getAccessToken(['repo']);

      // Use GraphQL mutation to mark PR as ready
      const response = await fetch('https://api.github.com/graphql', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation($pullRequestId: ID!) {
              markPullRequestReadyForReview(input: { pullRequestId: $pullRequestId }) {
                pullRequest {
                  id
                  isDraft
                }
              }
            }
          `,
          variables: {
            pullRequestId: pr.nodeId,
          },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to mark PR as ready');
      }

      setSnackbar({
        open: true,
        message: `PR #${pr.number} is now ready for review!`,
        severity: 'success',
      });

      // Refresh will be triggered by onRefreshPR in the list item
    } catch (err) {
      console.error('Failed to mark PR as ready:', err);
      setSnackbar({
        open: true,
        message: `Failed to mark PR #${pr.number} as ready: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  }, [octokit, githubAuthApi]);

  // Handle convert to draft action
  const handleConvertToDraft = useCallback(async (pr: PullRequestReview) => {
    if (!octokit) return;

    try {
      const accessToken = await githubAuthApi.getAccessToken(['repo']);

      // Use GraphQL mutation to convert PR to draft
      const response = await fetch('https://api.github.com/graphql', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation($pullRequestId: ID!) {
              convertPullRequestToDraft(input: { pullRequestId: $pullRequestId }) {
                pullRequest {
                  id
                  isDraft
                }
              }
            }
          `,
          variables: {
            pullRequestId: pr.nodeId,
          },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to convert PR to draft');
      }

      setSnackbar({
        open: true,
        message: `PR #${pr.number} converted to draft!`,
        severity: 'success',
      });

      // Refresh will be triggered by onRefreshPR in the list item
    } catch (err) {
      console.error('Failed to convert PR to draft:', err);
      setSnackbar({
        open: true,
        message: `Failed to convert PR #${pr.number} to draft: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  }, [octokit, githubAuthApi]);

  // Handle fetch diff action
  const handleFetchDiff = useCallback(async (pr: PullRequestReview): Promise<string> => {
    if (!octokit) {
      throw new Error('GitHub client not initialized');
    }

    try {
      // Fetch the diff from GitHub
      const response = await octokit.pulls.get({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        pull_number: pr.number,
        mediaType: {
          format: 'diff',
        },
      });

      // The response data will be a string when requesting diff format
      return response.data as unknown as string;
    } catch (err) {
      console.error('Failed to fetch diff:', err);
      throw new Error(`Failed to fetch diff: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [octokit]);

  // Handle fetch comments action
  const handleFetchComments = useCallback(async (pr: PullRequestReview): Promise<PullRequestComment[]> => {
    if (!octokit) {
      throw new Error('GitHub client not initialized');
    }

    try {
      const { data: comments } = await octokit.issues.listComments({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        issue_number: pr.number,
        per_page: 100,
      });

      return comments.map(c => ({
        id: c.id,
        body: c.body || '',
        author: {
          login: c.user?.login || 'unknown',
          avatarUrl: c.user?.avatar_url || '',
        },
        createdAt: c.created_at,
        updatedAt: c.updated_at,
        isBot: c.user?.type === 'Bot',
      }));
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      throw new Error(`Failed to fetch comments: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [octokit]);

  // Handle add comment action
  const handleAddComment = useCallback(async (pr: PullRequestReview, body: string): Promise<void> => {
    if (!octokit) {
      throw new Error('GitHub client not initialized');
    }

    try {
      await octokit.issues.createComment({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        issue_number: pr.number,
        body,
      });

      // Invalidate comment count cache for this PR
      githubClientCache.invalidatePR(pr.repository.owner, pr.repository.name, pr.number);

      setSnackbar({
        open: true,
        message: `Comment added to PR #${pr.number}!`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to add comment:', err);
      setSnackbar({
        open: true,
        message: `Failed to add comment: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
      throw err;
    }
  }, [octokit]);

  // Handle fetch reviews action
  const handleFetchReviews = useCallback(async (pr: PullRequestReview): Promise<PullRequestReviewItem[]> => {
    if (!octokit) {
      throw new Error('GitHub client not initialized');
    }

    try {
      const { data: reviews } = await octokit.pulls.listReviews({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        pull_number: pr.number,
        per_page: 100,
      });

      return reviews.map(r => ({
        id: r.id,
        body: r.body || '',
        state: r.state as PullRequestReviewItem['state'],
        author: {
          login: r.user?.login || 'unknown',
          avatarUrl: r.user?.avatar_url || '',
        },
        createdAt: r.submitted_at || '',
        submittedAt: r.submitted_at || undefined,
      }));
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      throw new Error(`Failed to fetch reviews: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [octokit]);

  // Handle add review action
  const handleAddReview = useCallback(async (
    pr: PullRequestReview,
    body: string,
    event: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES'
  ): Promise<void> => {
    if (!octokit) {
      throw new Error('GitHub client not initialized');
    }

    try {
      await octokit.pulls.createReview({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        pull_number: pr.number,
        body: body || undefined,
        event,
      });

      const eventLabels = {
        COMMENT: 'Comment submitted',
        APPROVE: 'PR approved',
        REQUEST_CHANGES: 'Changes requested',
      };

      setSnackbar({
        open: true,
        message: `${eventLabels[event]} on PR #${pr.number}!`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to add review:', err);
      setSnackbar({
        open: true,
        message: `Failed to add review: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
      throw err;
    }
  }, [octokit]);

  // Handle refresh single PR action - updates just one PR in the list
  const handleRefreshPR = useCallback(async (pr: PullRequestReview): Promise<void> => {
    if (!octokit) return;

    try {
      // Re-fetch the PR data
      const { data: searchResult } = await octokit.search.issuesAndPullRequests({
        q: `repo:${pr.repository.fullName} is:pr ${pr.number}`,
        per_page: 1,
      });

      if (searchResult.items.length === 0) return;

      const updatedPR = await processPR(octokit, searchResult.items[0], pr.relationship);
      if (!updatedPR) return;

      // Update the PR in the appropriate list
      setReviewRequestedPRs(prev =>
        prev.map(p => p.id === pr.id ? updatedPR : p)
      );
      setTeamReviewPRs(prev =>
        prev.map(p => p.id === pr.id ? { ...updatedPR, requestingTeamSlugs: p.requestingTeamSlugs } : p)
      );
      setUserRelatedPRs(prev =>
        prev.map(p => p.id === pr.id ? updatedPR : p)
      );
    } catch (err) {
      console.error('Failed to refresh PR:', err);
    }
  }, [octokit]);

  // Handle request reviewers action
  const handleRequestReviewers = useCallback(async (
    pr: PullRequestReview,
    reviewers: { users: string[]; teams: string[] },
  ): Promise<void> => {
    if (!octokit) {
      throw new Error('GitHub client not initialized');
    }

    const results: { success: string[]; failed: Array<{ name: string; reason: string }> } = {
      success: [],
      failed: [],
    };

    // Helper to extract error message
    const getErrorMessage = (err: unknown): string => {
      if (err instanceof Error) {
        // Parse GitHub API error messages
        const message = err.message;
        if (message.includes('not a collaborator')) {
          return 'Not a collaborator on this repository';
        }
        if (message.includes('Could not resolve')) {
          return 'User/team not found';
        }
        return message;
      }
      return 'Unknown error';
    };

    // Request user reviewers one by one to handle partial failures
    for (const user of reviewers.users) {
      try {
        await octokit.pulls.requestReviewers({
          owner: pr.repository.owner,
          repo: pr.repository.name,
          pull_number: pr.number,
          reviewers: [user],
        });
        results.success.push(user);
      } catch (err) {
        console.warn(`Failed to request review from user ${user}:`, err);
        results.failed.push({
          name: user,
          reason: getErrorMessage(err),
        });
      }
    }

    // Request team reviewers one by one to handle partial failures
    for (const team of reviewers.teams) {
      try {
        await octokit.pulls.requestReviewers({
          owner: pr.repository.owner,
          repo: pr.repository.name,
          pull_number: pr.number,
          team_reviewers: [team],
        });
        results.success.push(`team ${team}`);
      } catch (err) {
        console.warn(`Failed to request review from team ${team}:`, err);
        results.failed.push({
          name: `team ${team}`,
          reason: getErrorMessage(err),
        });
      }
    }

    // Build result message
    if (results.success.length > 0 && results.failed.length === 0) {
      // All succeeded
      setSnackbar({
        open: true,
        message: `Requested review from ${results.success.join(', ')} on PR #${pr.number}!`,
        severity: 'success',
      });
    } else if (results.success.length > 0 && results.failed.length > 0) {
      // Partial success
      const failedNames = results.failed.map(f => `${f.name} (${f.reason})`).join(', ');
      setSnackbar({
        open: true,
        message: `Requested review from ${results.success.join(', ')}. Failed: ${failedNames}`,
        severity: 'warning',
      });
    } else if (results.failed.length > 0) {
      // All failed
      const failedNames = results.failed.map(f => `${f.name} (${f.reason})`).join(', ');
      setSnackbar({
        open: true,
        message: `Failed to request reviewers: ${failedNames}`,
        severity: 'error',
      });
      throw new Error(`Failed to request reviewers: ${failedNames}`);
    }
  }, [octokit]);

  useEffect(() => {
    fetchPullRequests();
  }, [fetchPullRequests]);

  const handleRefresh = useCallback(() => {
    fetchPullRequests(true);
  }, [fetchPullRequests]);

  const handleHideSection = useCallback(() => {
    onHide?.();
  }, [onHide]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const totalPRCount = reviewRequestedPRs.length + teamReviewPRs.length + userRelatedPRs.length;

  if (loading) {
    return (
      <CollapsibleSection
        title="Pull Requests"
        sectionKey="pull-requests"
        editMode={editMode}
        defaultCollapsed={defaultCollapsed}
      >
        <Progress />
      </CollapsibleSection>
    );
  }

  if (error) {
    return (
      <CollapsibleSection
        title="Pull Requests"
        sectionKey="pull-requests"
        editMode={editMode}
        defaultCollapsed={defaultCollapsed}
        onHide={handleHideSection}
      >
        <Box className={classes.errorMessage}>
          <Typography variant="body2">{error}</Typography>
        </Box>
      </CollapsibleSection>
    );
  }

  return (
    <>
      <CollapsibleSection
        title="Pull Requests"
        sectionKey="pull-requests"
        defaultCollapsed={defaultCollapsed}
        onHide={handleHideSection}
        editMode={editMode}
        isEmpty={totalPRCount === 0}
        emptyMessage="No pull requests requiring your attention."
        persistentAction={
          <Tooltip title="Refresh">
            <Button
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleRefresh();
              }}
              className={classes.refreshButton}
              disabled={refreshing}
            >
              {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </Button>
          </Tooltip>
        }
      >
        {/* PRs awaiting your review */}
        {reviewRequestedPRs.length > 0 && (
          <Box className={classes.subsection}>
            <Box className={classes.subsectionTitle}>
              <RateReviewIcon fontSize="small" />
              <Typography variant="subtitle2">Awaiting Your Review</Typography>
              <Box className={`${classes.count} ${classes.urgentCount}`}>
                {reviewRequestedPRs.length}
              </Box>
            </Box>
            <Box className={classes.prList}>
              {reviewRequestedPRs.map(pr => (
                <PullRequestListItem
                  key={pr.id}
                  pr={pr}
                  currentUser={currentUser || undefined}
                  onMerge={handleMerge}
                  onMarkReady={handleMarkReady}
                  onConvertToDraft={handleConvertToDraft}
                  onFetchDiff={handleFetchDiff}
                  onFetchComments={handleFetchComments}
                  onAddComment={handleAddComment}
                  onFetchReviews={handleFetchReviews}
                  onAddReview={handleAddReview}
                  onRefreshPR={handleRefreshPR}
                  onRequestReviewers={handleRequestReviewers}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* PRs awaiting team review */}
        {teamReviewPRs.length > 0 && (
          <Box className={classes.subsection}>
            <Box className={classes.subsectionTitle}>
              <GroupIcon fontSize="small" />
              <Typography variant="subtitle2">Team Review Requested</Typography>
              <Box className={`${classes.count} ${classes.teamCount}`}>
                {teamReviewPRs.length}
              </Box>
            </Box>
            <Box className={classes.prList}>
              {teamReviewPRs.map(pr => (
                <Box key={pr.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <PullRequestListItem
                      pr={pr}
                      currentUser={currentUser || undefined}
                      onMerge={handleMerge}
                      onMarkReady={handleMarkReady}
                      onConvertToDraft={handleConvertToDraft}
                      onFetchDiff={handleFetchDiff}
                      onFetchComments={handleFetchComments}
                      onAddComment={handleAddComment}
                      onFetchReviews={handleFetchReviews}
                      onAddReview={handleAddReview}
                      onRefreshPR={handleRefreshPR}
                      onRequestReviewers={handleRequestReviewers}
                    />
                  </Box>
                  {pr.requestingTeamSlugs && pr.requestingTeamSlugs.length > 0 && (
                    <Box className={classes.teamChipsContainer}>
                      {pr.requestingTeamSlugs.map(teamSlug => (
                        <Chip
                          key={teamSlug}
                          label={teamSlug}
                          size="small"
                          variant="outlined"
                          className={classes.teamChip}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* User's PRs and involved PRs */}
        {userRelatedPRs.length > 0 && (
          <Box className={classes.subsection}>
            <Box className={classes.subsectionTitle}>
              <AssignmentIcon fontSize="small" />
              <Typography variant="subtitle2">Your PRs & Involved</Typography>
              <Box className={classes.count}>
                {userRelatedPRs.length}
              </Box>
            </Box>
            <Box className={classes.prList}>
              {userRelatedPRs.map(pr => (
                <PullRequestListItem
                  key={pr.id}
                  pr={pr}
                  currentUser={currentUser || undefined}
                  onMerge={handleMerge}
                  onMarkReady={handleMarkReady}
                  onConvertToDraft={handleConvertToDraft}
                  onFetchDiff={handleFetchDiff}
                  onFetchComments={handleFetchComments}
                  onAddComment={handleAddComment}
                  onFetchReviews={handleFetchReviews}
                  onAddReview={handleAddReview}
                  onRefreshPR={handleRefreshPR}
                  onRequestReviewers={handleRequestReviewers}
                />
              ))}
            </Box>
          </Box>
        )}

        {totalPRCount === 0 && (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              No pull requests requiring your attention.
            </Typography>
          </Box>
        )}
      </CollapsibleSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
